import {Permission} from '@sollapay/enums';
import {AppSidebar as GenericAppSidebar, LanguageSwitcher} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {config} from '@/app';
import {AuthNavAccountCard, usePermissions} from '@/features/authentication';

import {AppLogo, AppLogomark} from './AppLogo';
import {navGroups as navGroupConfigs} from '../config/nav-items.config';
import {APP_SIDEBAR_STORAGE_KEY} from '../config/sidebar.config';

import type {FC, ReactNode} from 'react';

interface AppSidebarProps {
  children: ReactNode;
}

// Nav items that require a specific permission beyond just being an authenticated firm member.
const NAV_ITEM_PERMISSIONS: Record<string, Permission> = {
  team: Permission.TEAM_READ
};

export const AppSidebar: FC<AppSidebarProps> = ({children}) => {
  const {t} = useTranslation('nav');
  const {hasPermission} = usePermissions();

  const navGroups = navGroupConfigs
    .map(group => ({
      id: group.id,
      label: t(group.labelKey),
      items: group.items
        .filter(item => {
          const requiredPermission = NAV_ITEM_PERMISSIONS[item.id];
          return !requiredPermission || hasPermission(requiredPermission);
        })
        .map(item => ({...item, label: t(item.labelKey)}))
    }))
    .filter(group => group.items.length > 0);

  return (
    <GenericAppSidebar
      logo={<AppLogo />}
      collapsedLogo={<AppLogomark />}
      footer={
        <>
          {config.features.enableLanguageSwitcher && <LanguageSwitcher />}
          <AuthNavAccountCard />
        </>
      }
      navGroups={navGroups}
      storageKey={APP_SIDEBAR_STORAGE_KEY}
    >
      {children}
    </GenericAppSidebar>
  );
};
